import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { acquisitionsApi, type AcqRequestDetail, type TimelineEvent } from "@/lib/api/acquisitions";
import { getApiErrorMessage } from "@/lib/api-error";
import { statusVariantMap } from "@/pages/acquisitions/AcquisitionRequestsPage";
import {
  Loader2,
  ArrowLeft,
  FileText,
  Send,
  Eye,
  HelpCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Package,
  MessageSquareReply,
  Info,
} from "lucide-react";

const timelineIcons: Record<string, React.ReactNode> = {
  REQUEST_CREATED: <FileText className="h-4 w-4 text-slate-500" />,
  REQUEST_SUBMITTED: <Send className="h-4 w-4 text-blue-500" />,
  REVIEW_STARTED: <Eye className="h-4 w-4 text-purple-500" />,
  INFO_REQUESTED: <HelpCircle className="h-4 w-4 text-amber-500" />,
  APPROVED: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  REJECTED: <XCircle className="h-4 w-4 text-red-500" />,
  REQUEST_UPDATED: <FileText className="h-4 w-4 text-slate-500" />,
  PO_CREATED: <Package className="h-4 w-4 text-blue-500" />,
};

function timelineHeading(ev: TimelineEvent): string {
  switch (ev.event) {
    case "REQUEST_CREATED":
      return "Request created";
    case "REQUEST_SUBMITTED":
      return "Submitted for review";
    case "REVIEW_STARTED":
      return "Review started";
    case "INFO_REQUESTED":
      return "Staff asked for more information";
    case "REQUEST_UPDATED":
      return "Details updated";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Not approved";
    default:
      return ev.event.replace(/_/g, " ");
  }
}

function timelineDetail(ev: TimelineEvent): string | null {
  const d = ev.data;
  if (!d || typeof d !== "object") return null;
  if (ev.event === "INFO_REQUESTED" && typeof d.question === "string" && d.question.trim()) {
    return d.question.trim();
  }
  if (ev.event === "REJECTED" && typeof d.reason === "string" && d.reason.trim()) {
    return d.reason.trim();
  }
  if (ev.event === "APPROVED" && typeof d.note === "string" && d.note.trim()) {
    return d.note.trim();
  }
  return null;
}

function latestStaffQuestion(timeline: TimelineEvent[]): string | null {
  for (let i = timeline.length - 1; i >= 0; i--) {
    const ev = timeline[i];
    if (ev.event !== "INFO_REQUESTED") continue;
    const q = timelineDetail(ev);
    if (q) return q;
  }
  return null;
}

type EditForm = {
  title: string;
  author: string;
  isbn: string;
  justification: string;
  urgency: string;
};

export default function MyRecommendationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const toast = useToast();
  const [request, setRequest] = useState<AcqRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EditForm>({
    title: "",
    author: "",
    isbn: "",
    justification: "",
    urgency: "MEDIUM",
  });

  const loadRequest = useCallback(async () => {
    if (!id || !user?.id) return;
    setLoading(true);
    try {
      const data = await acquisitionsApi.requests.getById(id);
      if (data.requesterId !== user.id) {
        setForbidden(true);
        setRequest(null);
      } else {
        setForbidden(false);
        setRequest(data);
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not load this request."));
    } finally {
      setLoading(false);
    }
  }, [id, user?.id, toast]);

  useEffect(() => {
    if (!id || authLoading) return;
    if (!user?.id) {
      setLoading(false);
      setForbidden(true);
      return;
    }
    void loadRequest();
  }, [id, user?.id, authLoading, loadRequest]);

  useEffect(() => {
    if (!request || request.status !== "NEEDS_INFO") return;
    setForm({
      title: request.title,
      author: request.author ?? "",
      isbn: request.isbn ?? "",
      justification: request.justification ?? "",
      urgency: request.urgency,
    });
  }, [
    request?.id,
    request?.status,
    request?.title,
    request?.author,
    request?.isbn,
    request?.justification,
    request?.urgency,
  ]);

  const staffQuestion = useMemo(
    () => (request ? latestStaffQuestion(request.timeline) : null),
    [request]
  );

  const updateField = (field: keyof EditForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleRespondSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !request || request.status !== "NEEDS_INFO") return;
    const title = form.title.trim();
    if (!title) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      const patch: Parameters<typeof acquisitionsApi.requests.update>[1] = {};
      if (title !== request.title) patch.title = title;
      const authorNext = form.author.trim();
      const authorPrev = (request.author ?? "").trim();
      if (authorNext !== authorPrev) patch.author = authorNext;
      const isbnNext = form.isbn.trim();
      const isbnPrev = (request.isbn ?? "").trim();
      if (isbnNext !== isbnPrev) patch.isbn = isbnNext;
      const justNext = form.justification.trim();
      const justPrev = (request.justification ?? "").trim();
      if (justNext !== justPrev) patch.justification = justNext;
      if (form.urgency !== request.urgency) patch.urgency = form.urgency;

      if (Object.keys(patch).length > 0) {
        await acquisitionsApi.requests.update(id, patch);
      }
      await acquisitionsApi.requests.submit(id);
      toast.success("Your response was sent back to staff.");
      const data = await acquisitionsApi.requests.getById(id);
      if (data.requesterId === user?.id) setRequest(data);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not send your response."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (forbidden || !request) {
    return (
      <div className="space-y-4 text-center py-16">
        <p className="text-slate-600">
          This recommendation was not found or you do not have access.
        </p>
        <Button asChild variant="outline">
          <Link to="/my-recommendations">Back to my recommendations</Link>
        </Button>
      </div>
    );
  }

  const cfg = statusVariantMap[request.status] || {
    variant: "neutral" as const,
    label: request.status,
  };
  const needsInfo = request.status === "NEEDS_INFO";

  return (
    <PageContainer>
      <PageHeader
        title={needsInfo ? form.title || request.title : request.title}
        description={
          needsInfo
            ? form.author || request.author
              ? `by ${form.author || request.author}`
              : undefined
            : request.author
              ? `by ${request.author}`
              : undefined
        }
        showSearch={false}
        titleSize="md"
        actions={
          <Button variant="outline" asChild>
            <Link to="/my-recommendations">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      {needsInfo && (
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>
          <StatusBadge variant="info">Your request</StatusBadge>
        </div>
      )}

      {needsInfo && (
        <Alert variant="info" className="items-start">
          <div className="flex gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-200 bg-white">
              <MessageSquareReply className="h-4 w-4 text-primary" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold text-slate-900">Staff need more information</p>
              <AlertDescription>
                {staffQuestion ? (
                  <span className="block whitespace-pre-wrap text-slate-700">{staffQuestion}</span>
                ) : (
                  <span className="text-slate-600">
                    Update the details below and send your response. Staff will continue reviewing
                    once you submit.
                  </span>
                )}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {needsInfo ? (
        <form onSubmit={handleRespondSubmit} noValidate>
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50">
                  <Info className="h-5 w-5 text-amber-700" aria-hidden />
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900">Your response</CardTitle>
                  <CardDescription className="mt-1">
                    Adjust the recommendation if needed, then send it back to the acquisitions team.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <div className="space-y-2">
                <Label htmlFor="resp-title">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="resp-title"
                  value={form.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  required
                  className="border-slate-200 bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resp-author">Author</Label>
                <Input
                  id="resp-author"
                  value={form.author}
                  onChange={(e) => updateField("author", e.target.value)}
                  className="border-slate-200 bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resp-isbn">ISBN</Label>
                <Input
                  id="resp-isbn"
                  value={form.isbn}
                  onChange={(e) => updateField("isbn", e.target.value)}
                  className="border-slate-200 bg-white font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resp-urgency">Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => updateField("urgency", v)}>
                  <SelectTrigger id="resp-urgency" className="border-slate-200 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="resp-justification">Note / answer to staff</Label>
                <Textarea
                  id="resp-justification"
                  value={form.justification}
                  onChange={(e) => updateField("justification", e.target.value)}
                  rows={5}
                  placeholder="Answer the question above or add any extra details…"
                  className="resize-none border-slate-200 bg-white"
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/60 py-5 sm:flex-row sm:justify-end">
              <Button type="submit" disabled={saving} className="min-h-11 w-full sm:w-auto">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send response to staff
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      ) : (
        <div className="rounded-lg border bg-white p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>
            <StatusBadge variant="info">Your request</StatusBadge>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div>
              <span className="text-slate-500">ISBN</span>
              <p className="font-medium">{request.isbn || "—"}</p>
            </div>
            <div>
              <span className="text-slate-500">Submitted</span>
              <p className="font-medium">{new Date(request.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <span className="text-slate-500">Urgency</span>
              <p className="font-medium">{request.urgency}</p>
            </div>
          </div>
          {request.justification && (
            <div>
              <span className="text-sm text-slate-500">Your note</span>
              <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">
                {request.justification}
              </p>
            </div>
          )}
          {request.rejectionReason && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <strong>Decision:</strong> {request.rejectionReason}
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg border bg-white p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Activity</h3>
        {request.timeline.length === 0 ? (
          <p className="text-sm text-slate-500">No events yet.</p>
        ) : (
          <div className="space-y-0">
            {request.timeline.map((ev, idx) => {
              const detail = timelineDetail(ev);
              return (
                <div key={ev.id} className="flex gap-3 pb-4">
                  <div className="flex flex-col items-center">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
                      {timelineIcons[ev.event] || <Clock className="h-4 w-4 text-slate-400" />}
                    </div>
                    {idx < request.timeline.length - 1 && (
                      <div className="flex-1 w-px bg-slate-200 mt-1 min-h-[1rem]" />
                    )}
                  </div>
                  <div className="flex-1 pt-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{timelineHeading(ev)}</p>
                    {detail && (
                      <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap border-l-2 border-slate-200 pl-3">
                        {detail}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(ev.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
